import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { TABLES } from '../data/tables';
import { Repository, BusinessVisitRepository, ContactLeadRepository } from './BaseRepository';
import { BusinessVisit, ContactLead } from '../data/types/analytics';

export class SupabaseBusinessVisitRepository implements BusinessVisitRepository {
  private client: SupabaseClient;
  private tableName = TABLES.BUSINESS_VISIT;

  constructor(client: SupabaseClient = supabase) {
    this.client = client;
  }

  async create(data: Partial<BusinessVisit>): Promise<BusinessVisit> {
    const { data: result, error } = await this.client
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async findById(id: string): Promise<BusinessVisit | null> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select()
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async findAll(): Promise<BusinessVisit[]> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select();

    if (error) throw error;
    return data;
  }

  async update(id: string, data: Partial<BusinessVisit>): Promise<BusinessVisit> {
    const { data: result, error } = await this.client
      .from(this.tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.client
      .from(this.tableName)
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async findByField(field: keyof BusinessVisit, value: any): Promise<BusinessVisit[]> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select()
      .eq(field, value);

    if (error) throw error;
    return data;
  }

  async findByBusinessAndIp(businessId: number, ipAddress: string): Promise<BusinessVisit | null> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const { data, error } = await this.client
      .from(this.tableName)
      .select()
      .eq('business_id', businessId)
      .eq('ip_address', ipAddress)
      .gte('created_at', today.toISOString())
      .lt('created_at', tomorrow.toISOString())
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  async incrementVisitCount(id: number): Promise<void> {
    // First get the current visit count
    const { data: visit, error: fetchError } = await this.client
      .from(this.tableName)
      .select('visit_count')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    // Then update with incremented count
    const { error: updateError } = await this.client
      .from(this.tableName)
      .update({ visit_count: (visit.visit_count || 0) + 1 })
      .eq('id', id);

    if (updateError) throw updateError;
  }
}

export class SupabaseContactLeadRepository implements ContactLeadRepository {
  private client: SupabaseClient;
  private tableName = TABLES.CONTACT_LEAD;

  constructor(client: SupabaseClient = supabase) {
    this.client = client;
  }

  async create(data: Partial<ContactLead>): Promise<ContactLead> {
    const { data: result, error } = await this.client
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async findById(id: string): Promise<ContactLead | null> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select()
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async findAll(): Promise<ContactLead[]> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select();

    if (error) throw error;
    return data;
  }

  async update(id: string, data: Partial<ContactLead>): Promise<ContactLead> {
    const { data: result, error } = await this.client
      .from(this.tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.client
      .from(this.tableName)
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async findByField(field: keyof ContactLead, value: any): Promise<ContactLead[]> {
    const { data, error } = await this.client
      .from(this.tableName)
      .select()
      .eq(field, value);

    if (error) throw error;
    return data;
  }
} 